import { useNextHashQueryParams } from '@hub/hooks/useNextQueryParams';
import type { StoreApi } from 'zustand';
import { createStore, useStore } from 'zustand';
import { createContext, useContext, useRef } from 'react';
import produce from 'immer';
import type { ParsedUrlQuery } from 'querystring';
import { IbhFilterKeys } from '@hub/libs/constants';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';
import { useDimensionsStore } from './useIbhDimensionsValuesStore';
import type { IbhProductionMethodResponseDto } from '@igus/iglidur-bearing-hub-api';
import type { IbhMaterialResponseDto } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { DimensionKey, IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';

export type IbhFilterValues = {
  [key in IbhFilterKeys]?: string | string[] | number | boolean | IbhDimensions;
};

export type IbhFilterControl = {
  [key in IbhFilterKeys]?: boolean | null | IbhDimensions;
};

export interface IbhFilterValuesStore {
  filterControl: IbhFilterControl;
  filterValues: IbhFilterValues;
  reset: () => void;
  resetSideBarFilters: () => void;
  setFilterValue: (
    key: IbhFilterKeys,
    value: string | string[] | number | boolean | undefined | IbhDimensions,
    options?: { preserveStandardMaterials?: boolean }
  ) => void;
}

export const createFilterValuesStore = (
  materials: IbhMaterialResponseDto[],
  productionMethods: IbhProductionMethodResponseDto[],
  selectedShape: ShapeDescriptor,
  query: ParsedUrlQuery
) =>
  createStore<IbhFilterValuesStore>(set => {
    const initialFilterValues: IbhFilterValues = { [IbhFilterKeys.MATERIAL]: [] };
    const filterKeys = Object.values(IbhFilterKeys);
    const storeDimensions = useDimensionsStore(state => state.dimensions);

    if (query) {
      Object.keys(query).forEach(key => {
        if (filterKeys.includes(key as IbhFilterKeys)) {
          if (key === IbhFilterKeys.MATERIAL) {
            const queryValues = (query[key] as string).split(',');
            initialFilterValues[key as IbhFilterKeys] = queryValues.filter(queryMaterial =>
              materials.map(material => material.id).includes(queryMaterial)
            );
          } else if (key === IbhFilterKeys.PRODUCTION_METHOD) {
            const queryValues = (query[key] as string).split(',');
            initialFilterValues[key as IbhFilterKeys] = queryValues.filter(queryProductionMethod =>
              productionMethods
                .filter(productionMethod => productionMethod.selectable)
                .map(productionMethod => productionMethod.productionMethod as string)
                .includes(queryProductionMethod)
            );
          } else if (key === IbhFilterKeys.DIMENSIONS && query[key as IbhFilterKeys]) {
            const dimensionParts = (query[key] as string).split('-');
            const dimensions = {} as IbhDimensions;
            if (dimensionParts.length > 0) {
              dimensionParts.forEach(dimensionPart => {
                const dimensionKey = dimensionPart.split(':')[0] as DimensionKey;
                const dimensionValue = dimensionPart.split(':')[1];
                if (dimensionValue !== '') {
                  dimensions[dimensionKey as keyof typeof dimensions] = parseFloat(dimensionValue);
                }
              });
              initialFilterValues[key as IbhFilterKeys] = { ...dimensions, ...storeDimensions[selectedShape.shapeKey] };
            }
          } else if (key === IbhFilterKeys.PROCEDURES) {
            const queryValues = (query[key] as string).split(',');
            initialFilterValues[key as IbhFilterKeys] = queryValues;
          } else if (
            key === IbhFilterKeys.MAX_SURFACE_PRESSURE ||
            key === IbhFilterKeys.TEMPERATURE_RANGE_MAX ||
            key === IbhFilterKeys.TEMPERATURE_RANGE_MIN
          ) {
            if (!Number.isNaN(parseInt(query[key] as string, 10))) {
              initialFilterValues[key as IbhFilterKeys] = parseInt(query[key] as string, 10);
            }
          } else if (query[key] === 'true') {
            initialFilterValues[key as IbhFilterKeys] = true;
          }
        }
      });
    }

    const currentSelectedMaterials = initialFilterValues[IbhFilterKeys.MATERIAL] as string[];

    const getFilterControl = (selectedMaterials: string[]) => {
      const newFilterControl: IbhFilterControl = {
        [IbhFilterKeys.FDA_COMPLIANCE]: null,
        [IbhFilterKeys.EU102011_COMPLIANCE]: null,
        [IbhFilterKeys.DIRT_AND_DUST]: null,
        [IbhFilterKeys.LOW_WATER_ABSORBTION]: null,
        [IbhFilterKeys.CHEMICALS]: null,
        [IbhFilterKeys.UNDERWATER_SPLASHWATER]: null,
        [IbhFilterKeys.ELECTRICALLY_CONDUCTIVE]: null,
        [IbhFilterKeys.HEAVY_DUTY]: null,
        [IbhFilterKeys.PTFE_FREE]: null,
        [IbhFilterKeys.PFAS_FREE]: null,
        [IbhFilterKeys.DIMENSIONS]: {} as IbhDimensions,
      };

      if (selectedMaterials && selectedMaterials.length > 0) {
        // Just uncommented for now to experience user behavior without constrains
        /*
        selectedMaterials.forEach(selectedMaterial => {
          const foundMaterial = materials.find(material => selectedMaterial === material.id);
          if (foundMaterial) {
            newFilterControl[IbhFilterKeys.FDA_COMPLIANCE] =
              newFilterControl[IbhFilterKeys.FDA_COMPLIANCE] === false
                ? newFilterControl[IbhFilterKeys.FDA_COMPLIANCE]
                : !foundMaterial.foodContactFDACompliant;
            newFilterControl[IbhFilterKeys.EU102011_COMPLIANCE] =
              newFilterControl[IbhFilterKeys.EU102011_COMPLIANCE] === false
                ? newFilterControl[IbhFilterKeys.EU102011_COMPLIANCE]
                : !foundMaterial.foodContactEUCompliant;
            newFilterControl[IbhFilterKeys.DIRT_AND_DUST] =
              newFilterControl[IbhFilterKeys.DIRT_AND_DUST] === false
                ? newFilterControl[IbhFilterKeys.DIRT_AND_DUST]
                : !foundMaterial.dirtResistant;
            newFilterControl[IbhFilterKeys.LOW_WATER_ABSORBTION] =
              newFilterControl[IbhFilterKeys.LOW_WATER_ABSORBTION] === false
                ? newFilterControl[IbhFilterKeys.LOW_WATER_ABSORBTION]
                : !foundMaterial.lowWaterAbsorption;
            newFilterControl[IbhFilterKeys.CHEMICALS] =
              newFilterControl[IbhFilterKeys.CHEMICALS] === false
                ? newFilterControl[IbhFilterKeys.CHEMICALS]
                : !foundMaterial.highChemicalResistance;
            newFilterControl[IbhFilterKeys.UNDERWATER_SPLASHWATER] =
              newFilterControl[IbhFilterKeys.UNDERWATER_SPLASHWATER] === false
                ? newFilterControl[IbhFilterKeys.UNDERWATER_SPLASHWATER]
                : !foundMaterial.underWaterCompliant;
            newFilterControl[IbhFilterKeys.ELECTRICALLY_CONDUCTIVE] =
              newFilterControl[IbhFilterKeys.ELECTRICALLY_CONDUCTIVE] === false
                ? newFilterControl[IbhFilterKeys.ELECTRICALLY_CONDUCTIVE]
                : !foundMaterial.electricallyConductive;
          }
        });
        */
      }

      return newFilterControl;
    };

    return {
      filterControl: getFilterControl(currentSelectedMaterials),
      filterValues: initialFilterValues,
      resetSideBarFilters: () => {
        set(state => ({
          ...state,
          filterValues: {
            dimensions: state.filterValues.dimensions,
          },
          filterControl: {
            dimensions: state.filterControl.dimensions,
          },
        }));
      },
      reset: () => {
        set(state => ({
          ...state,
          filterValues: {},
          filterControl: {},
        }));
      },
      setFilterValue: (key, value, { preserveStandardMaterials = false } = {}) => {
        set(
          produce<IbhFilterValuesStore>(state => {
            const currentState = state;

            if (currentState.filterValues === null) {
              return;
            }

            if (key === IbhFilterKeys.MATERIAL && !preserveStandardMaterials) {
              delete currentState.filterValues[IbhFilterKeys.US_STANDARD_MATERIALS];
            }

            if (
              (value && Array.isArray(value) && value.length > 0) ||
              (typeof value === 'boolean' && value) ||
              (typeof value === 'number' && !Number.isNaN(value)) ||
              (typeof value === 'string' && value) ||
              !!value
            ) {
              currentState.filterValues[key] = value;
            } else {
              delete currentState.filterValues[key];
            }

            currentState.filterControl = getFilterControl(
              currentState.filterValues[IbhFilterKeys.MATERIAL] as string[]
            );
          })
        );
      },
    };
  });

const StoreContext = createContext<StoreApi<IbhFilterValuesStore>>(null);

type IbhFilterValuesStoreProviderProps = {
  materials: IbhMaterialResponseDto[];
  productionMethods: IbhProductionMethodResponseDto[];
  selectedShape: ShapeDescriptor;
  children?: React.ReactNode;
};

export const IbhFilterValuesStoreProvider = ({
  materials,
  productionMethods,
  selectedShape,
  children,
}: IbhFilterValuesStoreProviderProps) => {
  const query = useNextHashQueryParams();
  const storeRef = useRef<StoreApi<IbhFilterValuesStore>>();
  storeRef.current = createFilterValuesStore(materials, productionMethods, selectedShape, query);

  return <StoreContext.Provider value={storeRef.current}>{children}</StoreContext.Provider>;
};

export const useIbhHasFilters = () => {
  const store = useContext(StoreContext);
  const values = useStore(store, state => state.filterValues);
  if (!values) {
    return false;
  }

  return (
    Object.keys(values)
      .filter(value => value !== IbhFilterKeys.DIMENSIONS)
      .flatMap(key => values[key as IbhFilterKeys]).length > 0
  );
};

export const useActiveFiltersAmount = (): number => {
  const store = useContext(StoreContext);
  const filterValues = useStore(store, state => state.filterValues);

  if (!filterValues) {
    return 0;
  }

  const excludedKeys = [IbhFilterKeys.DIMENSIONS, IbhFilterKeys.PROCEDURES, IbhFilterKeys.US_STANDARD_MATERIALS];

  const activeFilters = Object.entries(filterValues).filter(([key, value]) => {
    if (excludedKeys.includes(key as IbhFilterKeys)) {
      return false;
    }

    const isArrayValue = Array.isArray(value);
    const hasArrayItems = isArrayValue && value.length > 0;

    if (isArrayValue) {
      return hasArrayItems;
    }

    return value !== undefined && value !== null;
  });

  return activeFilters.length;
};

type getReturnType<T> = T extends (...args: never[]) => infer Return ? Return : never;
export const useIbhFilterValuesStore = (
  selector: (
    state: IbhFilterValuesStore
  ) => getReturnType<(state: IbhFilterValuesStore) => IbhFilterValuesStore[keyof IbhFilterValuesStore]>
) => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('Missing StoreProvider');
  }
  return useStore(store, selector);
};
